// Angular modules.
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ICalendarEvent } from 'app/interfaces/event.interface';
import { INotificationPreferences } from 'app/interfaces/notification-preferences.interface';
import { EventsService } from 'app/services/events.service';
import { NotificationsService } from 'app/services/notifications.service';
import { slideNotification } from 'app/utils/animations.util';
import * as secchiojs from 'secchiojs';

@Component({
	selector: 'app-notifications',
	templateUrl: './notifications.component.html',
	styleUrls: ['./notifications.component.scss'],
	animations: [slideNotification],
})
export class NotificationsComponent implements OnInit, OnDestroy {
	audio: HTMLAudioElement;
	event: ICalendarEvent;
	eventStatus: 'paid' | 'cancelled' | 'waiting' = 'waiting';
	eventUniqueCode: string;
	showNotification = false;
	interval: NodeJS.Timeout;
	preferences: INotificationPreferences = this.getPreferences();

	constructor(
		private eventsService: EventsService,
		private notificationsService: NotificationsService,
		private router: Router,
		private translateService: TranslateService
	) {}

	ngOnInit() {
		this.audio = new Audio('/assets/audio/session_alert.wav');
		this.audio.loop = true;
		this.eventsService.getNewOnlineSession().subscribe((event) => {
			this.event = event;

			if (this.event.isOnlinePayment) {
				this.event.userName = event.userName
					? event.userName
					: this.translateService.instant('GENERAL.UNIDENTIFIED');

				this.event.userImageUrl = event.userImageUrl
					? event.userImageUrl
					: '/assets/images/nophoto_blue_solid.png';

				if (this.event.id && this.event.profileId) {
					this.startNotification();
					this.interval = setInterval(async () => {
						await this.eventsService
							.getEvent(this.event.id)
							.then((updatedEvent) => {
								this.updateOnlineEventStatus(updatedEvent);
							});
					}, 2000);
				}
			}
		});
	}

	ngOnDestroy(): void {
		clearInterval(this.interval);
	}

	getPreferences(): INotificationPreferences {
		const bucketData = secchiojs.getBucket('zkNotifications')[0]();
		const defaultPrefs: INotificationPreferences = {
			soundAlert: true,
			notifiedEvents: [],
		};

		return bucketData || defaultPrefs;
	}

	setPreferences(newValues: INotificationPreferences) {
		const prefs = this.getPreferences();
		const bucketSetter = secchiojs.getBucket('zkNotifications')[1];
		const newBucket = { ...prefs, ...newValues };

		this.preferences = newBucket;
		bucketSetter(newBucket);
	}

	updateOnlineEventStatus(event): void {
		if (this.event.status === 'paid') {
			this.eventStatus = 'paid';
			this.startNotification();
		}

		if (!event || event.status === 'cancelled') {
			this.eventStatus = 'cancelled';
			this.audio.pause();
		}
	}

	startNotification(): void {
		const notifiedEvents = this.preferences.notifiedEvents;

		this.eventUniqueCode = `${this.event.id}_${this.eventStatus}`;

		if (!notifiedEvents.includes(this.eventUniqueCode)) {
			notifiedEvents.push(this.eventUniqueCode);
			this.setPreferences({ notifiedEvents, soundAlert: true });

			this.audio.play();
			this.showNotification = true;
		}
	}

	stopNotification(): void {
		this.audio.pause();
		this.showNotification = false;
	}

	openConference(): void {
		if (this.eventStatus === 'paid') {
			this.stopNotification();
			this.router.navigate([`session/${this.event.id}`]);
		}
	}

	toggleAudioAlert(state: boolean): void {
		state ? this.audio.play() : this.audio.pause();
	}

	showEventDetails() {
		this.notificationsService.showEventDetails(this.event.id);
	}
}
